import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import UpgradeModal from '../../../../common/upgrade-modal/UpgradeModal';
import { useGetUnreadCountQuery } from '../../../../../api/messagesApi';
import { useGetSubscriptionStatusQuery } from '../../../../../api/paymentsApi';
import { usePollingInterval } from '../../../../../hooks/usePollingInterval';
import { useAppSelector } from '../../../../../store';
import styles from './Sidebar.module.scss';

const cx = classNames.bind(styles);

interface User {
  id: number;
  name: string;
  role: string;
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  user: User | null;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, user }) => {
  const { t } = useTranslation();
  const { isImpersonating } = useAppSelector((s) => s.impersonation);
  // When impersonating, use the impersonated user's actual role (not always STUDENT)
  const effectiveRole = isImpersonating ? (user?.role === 'ADMIN' ? 'STUDENT' : user?.role || 'STUDENT') : user?.role;
  const isAdmin = effectiveRole === 'ADMIN';
  const isCurator = effectiveRole === 'CURATOR';
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const pollInterval = usePollingInterval(30000);
  const { data: unreadData } = useGetUnreadCountQuery(undefined, { pollingInterval: pollInterval });
  const unreadCount = unreadData?.unread_count ?? 0;
  const { data: subscription } = useGetSubscriptionStatusQuery(undefined, { skip: isAdmin || isCurator });
  const isPremium = subscription?.tier === "PREMIUM" && subscription?.status === "active";

  const studentNavItems: NavItem[] = [
    { path: '/dashboard', label: t('sidebar.dashboard', 'Dashboard'), icon: 'fa-home', end: true },
    { path: '/courses', label: t('sidebar.courses', 'Courses'), icon: 'fa-book' },
    { path: '/exercises', label: t('sidebar.exercises', 'Exercises'), icon: 'fa-dumbbell' },
    { path: '/vocabulary', label: t('sidebar.vocabulary', 'Vocabulary'), icon: 'fa-language' },
    // { path: '/schedule', label: t('sidebar.schedule', 'Schedule'), icon: 'fa-calendar-alt' },
    // { path: '/instructors', label: t('sidebar.instructors', 'Instructors'), icon: 'fa-chalkboard-teacher' },
    { path: '/messages', label: t('sidebar.messages', 'Messages'), icon: 'fa-comment-dots' },
    { path: '/profile', label: t('sidebar.profile', 'Profile'), icon: 'fa-user' },
    { path: '/settings', label: t('sidebar.settings', 'Settings'), icon: 'fa-cog' },
  ];

  const adminNavItems: NavItem[] = [
    { path: '/admin', label: t('sidebar.dashboard', 'Dashboard'), icon: 'fa-th-large', end: true },
    { path: '/admin/courses', label: t('sidebar.courses', 'Courses'), icon: 'fa-book' },
    { path: '/exercises', label: t('sidebar.exercises', 'Exercises'), icon: 'fa-dumbbell' },
    { path: '/admin/vocabulary', label: t('sidebar.vocabulary', 'Vocabulary'), icon: 'fa-language' },
    { path: '/admin/translations', label: t('sidebar.translations', 'Translations'), icon: 'fa-globe' },
    // { path: '/admin/schedule', label: t('sidebar.schedule', 'Schedule'), icon: 'fa-calendar-alt' },
    { path: '/admin/users', label: t('sidebar.students', 'Students'), icon: 'fa-user-graduate' },
    // { path: '/admin/resources', label: t('sidebar.resource', 'Resource'), icon: 'fa-paperclip' },
    { path: '/admin/transactions', label: t('sidebar.transaction', 'Transaction'), icon: 'fa-money-bill-wave' },
    { path: '/admin/messages', label: t('sidebar.messages', 'Messages'), icon: 'fa-comment-dots' },
    { path: '/admin/reviews', label: t('sidebar.reviews', 'Reviews'), icon: 'fa-star' },
    { path: '/admin/ai-costs', label: t('sidebar.aiCosts', 'AI Costs'), icon: 'fa-dollar-sign' },
    { path: '/admin/logs', label: t('sidebar.logs', 'Logs'), icon: 'fa-file-alt' },
    { path: '/admin/bug-reports', label: t('sidebar.bugReports', 'Bug Reports'), icon: 'fa-bug' },
    { path: '/admin/profile', label: t('sidebar.profile', 'Profile'), icon: 'fa-user' },
    { path: '/admin/settings', label: t('sidebar.settings', 'Settings'), icon: 'fa-cog' },
  ];

  const curatorNavItems: NavItem[] = [
    { path: '/curator', label: t('sidebar.dashboard', 'Dashboard'), icon: 'fa-th-large', end: true },
    { path: '/curator/users', label: t('sidebar.students', 'Students'), icon: 'fa-user-graduate' },
    { path: '/curator/courses', label: t('sidebar.courses', 'Courses'), icon: 'fa-book' },
    { path: '/curator/vocabulary', label: t('sidebar.vocabulary', 'Vocabulary'), icon: 'fa-language' },
    { path: '/curator/messages', label: t('sidebar.messages', 'Messages'), icon: 'fa-comment-dots' },
    { path: '/curator/profile', label: t('sidebar.profile', 'Profile'), icon: 'fa-user' },
    { path: '/curator/settings', label: t('sidebar.settings', 'Settings'), icon: 'fa-cog' },
  ];

  const navItems = isAdmin ? adminNavItems : isCurator ? curatorNavItems : studentNavItems;

  return (
    <aside className={cx('sidebar', { collapsed })}>
      <NavLink to={isAdmin ? '/admin' : isCurator ? '/curator' : '/dashboard'} className={cx('logo')}>
        <span className={cx('logoText')}>
          <span className={cx('logoMax')}>Nicolas</span>
          <span className={cx('logoSkills')}> </span>
          <span className={cx('logoDot')}>Smith</span>
        </span>
        <div className={cx('logoIcon')}>
          <span className={cx('logoMax')}>NS</span>
        </div>
      </NavLink>

      <nav className={cx('navigation')} data-tour="sidebar-nav">
        {navItems.map((item) => {
          const isMessagesItem = item.path.endsWith('/messages');
          const tourAttr = !isAdmin && !isCurator ? (
            item.path === '/courses' ? 'nav-courses' :
            item.path === '/exercises' ? 'nav-exercises' :
            item.path === '/vocabulary' ? 'nav-vocabulary' :
            undefined
          ) : undefined;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                cx('navItem', { active: isActive })
              }
              onClick={() => {
                if (collapsed && window.innerWidth <= 768) {
                  onToggle();
                }
              }}
              {...(tourAttr ? { 'data-tour': tourAttr } : {})}
            >
              <i className={`fas ${item.icon}`}></i>
              <span className={cx('navLabel')}>{item.label}</span>
              {isMessagesItem && unreadCount > 0 && (
                <span className={cx('navBadge')}>{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {!isAdmin && !isCurator && !isPremium && (
        <div className={cx('upgradeCard')}>
          <div className={cx('upgradeContent')}>
            <div className={cx('upgradeText')}>
              <h4>{t('sidebar.upgradeAccount', 'Upgrade your')}</h4>
              <h4><strong>{t('sidebar.accountToPro', 'Account to Pro')}</strong></h4>
            </div>
            <div className={cx('upgradeEmoji')}>🚀</div>
          </div>
          <p>{t('sidebar.upgradeDescription', 'Upgrade to premium to get premium features')}</p>
          <button className={cx('upgradeButton')} onClick={() => setShowUpgradeModal(true)}>
            {t('sidebar.upgrade', 'Upgrade')}
          </button>
        </div>
      )}

      <button className={cx('toggleButton')} onClick={onToggle}>
        <i className={`fas ${collapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
      </button>

      {!isAdmin && !isCurator && !isPremium && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </aside>
  );
};

export default Sidebar;
